<template>
    <div>
        <div
            class="grid items-center px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 md:grid-cols-12 md:gap-4"
        >
            <div class="hidden md:block md:col-span-1">
                <Button class="justify-start w-full" color="brand" @click="previous">
                    <icon icon="hero/outline/chevron-left" class="w-5 h-5" />
                </Button>
            </div>
            <div class="md:col-span-3">
                <Field
                    v-model="schedule.from"
                    :field="{
                        name: lang(`${langPrefix}.fields.schedule_from`),
                        slug: 'schedule_from',
                        adminComponent: 'AdminDate',
                    }"
                    dusk="form-schedule_from"
                    label-position="none"
                />
            </div>
            <div class="relative text-center text-gray-400 md:col-span-4 dark:text-zinc-600">
                <Float
                    placement="bottom-start"
                    :offset="6"
                    composable
                    enter="transition duration-200 ease-out"
                    enter-from="opacity-0 -translate-y-1"
                    enter-to="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leave-from="opacity-100 translate-y-0"
                    leave-to="opacity-0 -translate-y-1"
                    :show="focusedSearch"
                    floating-as="template"
                >
                    <FloatReference>
                        <Field
                            v-model="search"
                            :field="{
                                name: lang(`${langPrefix}.fields.search`),
                                slug: 'search',
                                type: 'search',
                                adminComponent: 'AdminSearch',
                                placeholder: lang(`${langPrefix}.fields.search_placeholder`),
                            }"
                            :class="{
                                'animate-pulse': isCurrentlySearching,
                            }"
                            dusk="form-schedule_search"
                            label-position="none"
                            autocomplete="off"
                            @focus="focusedSearch = true"
                            @blur="focusedSearch = false"
                        ></Field>
                    </FloatReference>

                    <FloatContent>
                        <ui-popover class="!w-full">
                            <ui-popover-section>
                                <ui-popover-segment v-if="isWithoutSearchPhrase">
                                    <div class="p-4 text-center">
                                        {{ lang(`${langPrefix}.search.empty`) }}
                                    </div>
                                </ui-popover-segment>
                                <ui-popover-segment v-else-if="isCurrentlySearching">
                                    <div class="p-4 text-center">
                                        {{ lang(`${langPrefix}.search.searching`) }}
                                    </div>
                                </ui-popover-segment>
                                <ui-popover-segment v-else-if="isSearchTooShort">
                                    <div class="p-4 text-center">
                                        {{ lang(`${langPrefix}.search.min_length`) }}
                                    </div>
                                </ui-popover-segment>
                                <ui-popover-segment v-else-if="isWithoutSearchResults">
                                    <div class="p-4 text-center">
                                        {{ lang(`${langPrefix}.search.no_results`) }}
                                    </div>
                                </ui-popover-segment>
                                <ui-popover-segment v-else>
                                    <ui-scrollable class="max-h-[400px]">
                                        <ui-popover-link
                                            v-for="result in results"
                                            :key="result.id"
                                            :clickable="true"
                                            class="!grid !w-full grid-cols-12"
                                            @click="showReservation(result.metable.reservation.slottable)"
                                        >
                                            <div class="flex flex-col col-span-10 pr-4 text-left">
                                                <div class="font-bold text-black dark:text-white">
                                                    {{ result.data.name }}
                                                </div>
                                                <div class="flex space-x-2">
                                                    <span v-if="result.data.email">{{ result.data.email }}</span>
                                                    <span v-if="result.data.phone">{{ result.data.phone }}</span>
                                                </div>
                                            </div>
                                            <div
                                                v-if="
                                                    result.metable &&
                                                    result.metable.reservation &&
                                                    result.metable.reservation.slottable &&
                                                    result.metable.reservation.slottable.data &&
                                                    result.metable.reservation.slottable.data.date
                                                "
                                                class="flex flex-col col-span-2 text-right"
                                            >
                                                <div>
                                                    {{
                                                        dateHumanReadableWithoutYear(
                                                            result.metable.reservation.slottable.data.date,
                                                        )
                                                    }}
                                                </div>
                                                <div>
                                                    {{ result.metable.reservation.slottable.data.time }}
                                                </div>
                                            </div>
                                        </ui-popover-link>
                                    </ui-scrollable>
                                </ui-popover-segment>
                            </ui-popover-section>
                        </ui-popover>
                    </FloatContent>
                </Float>
            </div>
            <div class="md:col-span-3">
                <Field
                    v-model="schedule.to"
                    :field="{
                        name: lang(`${langPrefix}.fields.schedule_to`),
                        slug: 'schedule_to',
                        adminComponent: 'AdminDate',
                    }"
                    dusk="form-schedule_to"
                    label-position="none"
                />
            </div>
            <div class="hidden md:block md:col-span-1">
                <Button class="justify-end w-full" color="brand" @click="next">
                    <icon icon="hero/outline/chevron-right" class="w-5 h-5" />
                </Button>
            </div>
        </div>

        <div class="hidden">
            <Field
                v-model="schedule.group"
                :field="{
                    name: lang(`${langPrefix}.fields.group_by`),
                    slug: 'group_by',
                    adminComponent: 'AdminSelect',
                    type: 'select',
                }"
                dusk="form-group_by"
                label-position="none"
                :options="[
                    {
                        id: 'day',
                        name: lang(`${langPrefix}.groups.day`),
                    },
                ]"
            />
        </div>

        <view-tree
            :tree="tree"
            :schedule="schedule"
            :metas="metas"
            :groups="groups"
            :show-advanced="showAdvanced"
            :calculated-name="calculatedName"
            :select-color="selectColor"
            :mass-create="massCreate"
            @dialog:open="dialogOpened"
            @dialog:close="dialogClosed"
        ></view-tree>

        <!-- Create reservable -->
        <create-reservable
            v-if="showingCreateReservable"
            :reservables="props.reservables"
            :fields="props.fields.reservable"
            :domains="props.domains"
            @close="showingCreateReservable = false"
        ></create-reservable>

        <div v-if="showAdvanced" class="py-2 text-right">
            <Button @click="showCreateReservable">
                <icon icon="hero/outline/plus" class="w-4 h-4 mr-2" />
                {{ lang('reservations::reservables.actions.create') }}
            </Button>
        </div>

        <show-slot
            v-if="showing !== false"
            :reservation-slot="showing"
            :widgets="showingWidgets"
            :metas="metas"
            @close="showing = false"
        ></show-slot>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Reservable } from '@src/Reservations/Pages/types';
import { DateTime } from 'luxon';
import { onKeyStroke } from '@vueuse/core';
import ViewTree from '@src/Reservations/Pages/Schedule/ViewTree.vue';
import CreateReservable from '@src/Reservations/Pages/Schedule/CreateReservable.vue';
import ShowSlot from '@src/Reservations/Pages/Schedule/ShowSlot.vue';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';
import { dateHumanReadableWithoutYear } from '@src/Common/Helpers/dates';
import { lang } from '@/lang/composable';
import { useToast } from '@src/Common/Composables/useToast';

interface Props {
    reservables?: Reservable[];
    schedule?: {
        from: string | null;
        to: string | null;
        group: string;
    };
    metas?: {
        [key: string]: string;
    } | null;
    groups?: any[] | object;
    fields: object;
    domains: any[] | null;
    showAdvanced?: boolean;
    selectColor?: boolean;
    massCreate?: boolean;
    calculatedName?: boolean;
    maxPeriodDays?: number;
}

const props = withDefaults(defineProps<Props>(), {
    reservables: () => [],
    schedule: () => ({
        from: null,
        to: null,
        group: 'day',
    }),
    metas: null,
    groups: () => [],
    showAdvanced: false,
    selectColor: false,
    massCreate: false,
    calculatedName: false,
    maxPeriodDays: 31,
});

const prefix = 'reservations/schedule';
const langPrefix = `${prefix}`;

const reservables = ref(props.reservables as Reservable[]);
const groups = ref(props.groups);

const metas = ref(props.metas);

const showAdvanced = ref(props.showAdvanced);
const calculatedName = ref(props.calculatedName);

const schedule = ref({
    from: props.schedule.from,
    to: props.schedule.to,
    group: props.schedule.group ?? 'day',
});

/**
 * Keep the selected window within the allowed maximum so the backend never
 * has to load an over-wide range (prevents the schedule data OOM).
 */
watch(
    () => [schedule.value.from, schedule.value.to],
    () => {
        if (!schedule.value.from || !schedule.value.to) {
            return;
        }

        const from = DateTime.fromISO(schedule.value.from);
        const to = DateTime.fromISO(schedule.value.to);

        if (!from.isValid || !to.isValid) {
            return;
        }

        if (Math.ceil(to.diff(from, 'days').days) <= props.maxPeriodDays) {
            return;
        }

        schedule.value.to = from.plus({ days: props.maxPeriodDays }).toISODate();
        useToast({
            message: lang(`${langPrefix}.messages.period_clamped`, { days: props.maxPeriodDays }),
            type: 'info',
        });
    },
);

const tree = computed(() => {
    const tree = [];
    const map = {};

    for (const reservable of reservables.value) {
        map[reservable.id] = reservable;
        reservable.children = [];
    }

    for (const reservable of reservables.value) {
        if (reservable.parent_id) {
            map[reservable.parent_id].children.push(reservable);
        } else {
            tree.push(reservable);
        }
    }

    return tree;
});

/**
 * Create reservable
 */
const showingCreateReservable = ref(false);

const showCreateReservable = () => {
    showingCreateReservable.value = true;
};

/**
 * Traversing dates
 */
const previous = () => {
    const from = DateTime.fromISO(schedule.value.from);
    const to = DateTime.fromISO(schedule.value.to);

    const diff = Math.ceil(to.diff(from, ['days']).days);

    schedule.value.from = from.minus({ days: diff }).toISODate();
    schedule.value.to = to.minus({ days: diff }).toISODate();
};

const next = () => {
    const from = DateTime.fromISO(schedule.value.from);
    const to = DateTime.fromISO(schedule.value.to);

    const diff = Math.ceil(to.diff(from, ['days']).days);

    schedule.value.from = from.plus({ days: diff }).toISODate();
    schedule.value.to = to.plus({ days: diff }).toISODate();
};

const isDialogOpened = ref(false);

const dialogOpened = () => {
    isDialogOpened.value = true;
};

const dialogClosed = () => {
    isDialogOpened.value = false;
};

onKeyStroke('ArrowLeft', (e) => {
    if (isDialogOpened.value) {
        return;
    }

    if (document.activeElement.tagName === 'INPUT') {
        return;
    }

    e.preventDefault();
    previous();
});

onKeyStroke('ArrowRight', (e) => {
    if (isDialogOpened.value) {
        return;
    }

    if (document.activeElement.tagName === 'INPUT') {
        return;
    }

    e.preventDefault();
    next();
});

/**
 * Search
 */
const search = ref('');
const searching = ref(false);
const results = ref([]);
const showResults = ref(false);
const focusedSearch = ref(false);

let triggerSearchTimeout = null;
const triggerSearchDelay = 500;

const searchSchedule = () => {
    searching.value = true;

    axios
        .get(route('reservations.schedule.search'), {
            params: {
                search: search.value,
            },
        })
        .then(({ data }) => {
            results.value = data.results;

            showResults.value = true;

            searching.value = false;
        });
};

watch(search, () => {
    if (!search.value || search.value.length < 3) {
        results.value = [];
        searching.value = false;
        return;
    }

    if (triggerSearchTimeout) {
        clearTimeout(triggerSearchTimeout);
    }

    triggerSearchTimeout = setTimeout(() => {
        searchSchedule();
    }, triggerSearchDelay);
});

const isWithoutSearchPhrase = computed(() => {
    return !search.value;
});

const isCurrentlySearching = computed(() => {
    return searching.value;
});

const isSearchTooShort = computed(() => {
    return search.value.length > 0 && search.value.length < 3;
});

const isWithoutSearchResults = computed(() => {
    return results.value.length === 0;
});

/**
 * Show reservation from search
 */
const showing = ref(false);
const showingWidgets = ref<Record<string, any[]>>({});

const showReservation = (slot: any) => {
    axios.get(route('reservations/slots.admin.schedule.slot.data', { id: slot.id })).then(({ data }) => {
        showing.value = data.slot;
        showingWidgets.value = data.widgets ?? {};
    });
};
</script>
